import { COLORS } from "../../shared/constants";
import { IconX, IconTrash, IconPlus } from "../../shared/icons";
import { useState } from "react";

export default function ScaleBuilderForm({ scale, onSave, onCancel }) {
  const isEdit = !!scale;
  const [form, setForm] = useState({
    name: scale?.name || "",
    short_name: scale?.short_name || "",
    description: scale?.description || "",
    administration_mode: scale?.administration_mode || "SELF_REPORT",
    scoring_method: scale?.scoring_method || "SUM",
    max_score: scale?.max_score ?? "",
    response_threshold_pct: scale?.response_threshold_pct ?? 50,
    remission_cutoff: scale?.remission_cutoff ?? "",
  });
  const [questions, setQuestions] = useState(scale?.questions || []);
  const [bands, setBands] = useState(scale?.severity_bands || []);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const h = (f, v) => { setForm(p => ({ ...p, [f]: v })); setErrors({}); };

  const addQuestion = () => {
    const num = questions.length + 1;
    setQuestions(prev => [...prev, {
      _key: `new-${Date.now()}`, question_number: num, question_text: "",
      response_type: "LIKERT", response_options: [
        { value: 0, label: "Not at all" }, { value: 1, label: "Several days" },
        { value: 2, label: "More than half the days" }, { value: 3, label: "Nearly every day" },
      ],
      sort_order: num,
    }]);
  };

  const handleResponseTypeChange = (idx, newType) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== idx) return q;
      // When switching to FREE_TEXT, clear response_options; switching back restores defaults
      if (newType === "FREE_TEXT") {
        return { ...q, response_type: newType, response_options: null };
      }
      if (newType === "VAS") {
        return { ...q, response_type: newType, response_options: null };
      }
      // LIKERT / CHOICE — restore options if none exist
      return {
        ...q,
        response_type: newType,
        response_options: Array.isArray(q.response_options) && q.response_options.length > 0
          ? q.response_options
          : [{ value: 0, label: "Not at all" }, { value: 1, label: "Several days" },
             { value: 2, label: "More than half the days" }, { value: 3, label: "Nearly every day" }],
      };
    }));
  };

  const updateQuestion = (idx, field, value) => {
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  };

  const removeQuestion = (idx) => {
    setQuestions(prev => prev.filter((_, i) => i !== idx).map((q, i) => ({ ...q, question_number: i + 1, sort_order: i + 1 })));
  };

  const addBand = () => {
    setBands(prev => [...prev, { _key: `band-${Date.now()}`, label: "", min_score: 0, max_score: 0, color: "#6b7280" }]);
  };

  const updateBand = (idx, field, value) => {
    setBands(prev => prev.map((b, i) => i === idx ? { ...b, [field]: value } : b));
  };

  const removeBand = (idx) => {
    setBands(prev => prev.filter((_, i) => i !== idx));
  };

  const updateOption = (qIdx, optIdx, field, value) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx || !Array.isArray(q.response_options)) return q;
      const opts = [...q.response_options];
      opts[optIdx] = { ...opts[optIdx], [field]: field === "value" ? parseInt(value) || 0 : value };
      return { ...q, response_options: opts };
    }));
  };

  const addOption = (qIdx) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx || !Array.isArray(q.response_options || [])) return q;
      const opts = [...(Array.isArray(q.response_options) ? q.response_options : [])];
      const nextVal = opts.length > 0 ? Math.max(...opts.map(o => o.value)) + 1 : 0;
      return { ...q, response_options: [...opts, { value: nextVal, label: "" }] };
    }));
  };

  const removeOption = (qIdx, optIdx) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx || !Array.isArray(q.response_options)) return q;
      return { ...q, response_options: q.response_options.filter((_, j) => j !== optIdx) };
    }));
  };

  const handleSubmit = async () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!form.short_name.trim()) errs.short_name = "Required";
    if (!form.max_score && form.max_score !== 0) errs.max_score = "Required";
    if (questions.length === 0) errs.questions = "At least one question required";
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    try {
      await onSave({
        ...form,
        id: scale?.id,
        max_score: parseInt(form.max_score),
        response_threshold_pct: parseInt(form.response_threshold_pct) || 50,
        remission_cutoff: form.remission_cutoff !== "" ? parseInt(form.remission_cutoff) : null,
        questions: questions.map((q, i) => ({
          question_number: i + 1,
          question_text: q.question_text,
          response_type: q.response_type || "LIKERT",
          response_options: q.response_options,
          vas_min: q.vas_min != null ? q.vas_min : null,
          vas_max: q.vas_max != null ? q.vas_max : null,
          vas_min_label: q.vas_min_label || null,
          vas_max_label: q.vas_max_label || null,
          sort_order: i + 1,
        })),
        severity_bands: bands.map(b => ({
          label: b.label,
          min_score: parseInt(b.min_score) || 0,
          max_score: parseInt(b.max_score) || 0,
          color: b.color,
        })),
      });
    } finally {
      setSaving(false);
    }
  };

  const selStyle = { width: "100%", height: "38px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, fontSize: "13px", color: COLORS.text, background: COLORS.surface, boxSizing: "border-box" };
  const bandColors = ["#22c55e", "#eab308", "#f97316", "#ef4444", "#991b1b", "#6b7280", "#1A56DB", "#6A3DB5"];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, margin: 0 }}>{isEdit ? `Edit: ${scale.short_name}` : "New Assessment Scale"}</h3>
        <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.textSecondary }}><IconX /></button>
      </div>

      {/* Basic info */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Full Name {errors.name && <span style={{ color: COLORS.danger }}>*</span>}</label>
          <input style={{ ...selStyle, borderColor: errors.name ? COLORS.danger : COLORS.border }} value={form.name} onChange={e => h("name", e.target.value)} placeholder="e.g., Patient Health Questionnaire" />
        </div>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Short Name {errors.short_name && <span style={{ color: COLORS.danger }}>*</span>}</label>
          <input style={{ ...selStyle, borderColor: errors.short_name ? COLORS.danger : COLORS.border }} value={form.short_name} onChange={e => h("short_name", e.target.value)} placeholder="e.g., PHQ-9" />
        </div>
      </div>
      <div style={{ marginBottom: "16px" }}>
        <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Description</label>
        <input style={selStyle} value={form.description} onChange={e => h("description", e.target.value)} placeholder="Brief description or instructions" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px", marginBottom: "20px" }}>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Administration</label>
          <select style={selStyle} value={form.administration_mode} onChange={e => h("administration_mode", e.target.value)}>
            <option value="SELF_REPORT">Self-Report</option>
            <option value="CLINICIAN">Clinician</option>
            <option value="BOTH">Both</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Max Score {errors.max_score && <span style={{ color: COLORS.danger }}>*</span>}</label>
          <input type="number" min="1" style={{ ...selStyle, borderColor: errors.max_score ? COLORS.danger : COLORS.border }} value={form.max_score} onChange={e => h("max_score", e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Response ≥ % reduction</label>
          <input type="number" min="1" max="100" style={selStyle} value={form.response_threshold_pct} onChange={e => h("response_threshold_pct", e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Remission cutoff ≤</label>
          <input type="number" min="0" style={selStyle} value={form.remission_cutoff} onChange={e => h("remission_cutoff", e.target.value)} placeholder="optional" />
        </div>
      </div>

      {/* Questions */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <label style={{ fontSize: "13px", fontWeight: "600", color: COLORS.text }}>Questions ({questions.length}) {errors.questions && <span style={{ color: COLORS.danger, fontSize: "11px" }}>— {errors.questions}</span>}</label>
          <button onClick={addQuestion}
            style={{ height: "28px", padding: "0 12px", borderRadius: "6px", border: "none", background: COLORS.primary, color: "#FFF", fontSize: "11px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
            <IconPlus /> Add Question
          </button>
        </div>
        {questions.map((q, qIdx) => (
          <div key={q.id || q._key || qIdx} style={{ padding: "12px", background: COLORS.inputBg, borderRadius: "8px", border: `1px solid ${COLORS.borderLight}`, marginBottom: "8px" }}>
            <div style={{ display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: "8px" }}>
              <span style={{ fontSize: "13px", fontWeight: "700", color: COLORS.primary, minWidth: "24px", marginTop: "8px" }}>{qIdx + 1}.</span>
              <input style={{ ...selStyle, flex: 1 }} value={q.question_text} onChange={e => updateQuestion(qIdx, "question_text", e.target.value)} placeholder="Question text" />
              <select value={q.response_type || "LIKERT"} onChange={e => handleResponseTypeChange(qIdx, e.target.value)}
                style={{ height: "38px", padding: "0 6px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "11px", color: COLORS.text, background: COLORS.surface, width: "96px" }}>
                <option value="LIKERT">Likert</option>
                <option value="VAS">VAS</option>
                <option value="CHOICE">Choice</option>
                <option value="FREE_TEXT">Free Text</option>
              </select>
              <button onClick={() => removeQuestion(qIdx)}
                style={{ height: "38px", width: "38px", borderRadius: "8px", border: `1px solid ${COLORS.dangerBorder}`, background: COLORS.dangerLight, color: COLORS.danger, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <IconTrash />
              </button>
            </div>
            {/* Response options — only for LIKERT/CHOICE with array response_options */}
            {(q.response_type === "LIKERT" || q.response_type === "CHOICE" || (!q.response_type && Array.isArray(q.response_options))) && (
            <div style={{ marginLeft: "32px" }}>
              <div style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", textTransform: "uppercase" }}>Response Options</div>
              {(Array.isArray(q.response_options) ? q.response_options : []).map((opt, optIdx) => (
                <div key={optIdx} style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "4px" }}>
                  <input type="number" style={{ width: "50px", height: "28px", padding: "0 6px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px", textAlign: "center" }}
                    value={opt.value} onChange={e => updateOption(qIdx, optIdx, "value", e.target.value)} />
                  <input style={{ flex: 1, height: "28px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px" }}
                    value={opt.label} onChange={e => updateOption(qIdx, optIdx, "label", e.target.value)} placeholder="Label (optional)" />
                  <button onClick={() => removeOption(qIdx, optIdx)}
                    style={{ height: "28px", width: "28px", borderRadius: "6px", border: `1px solid ${COLORS.dangerBorder}`, background: COLORS.dangerLight, color: COLORS.danger, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px" }}>✕</button>
                </div>
              ))}
              <button onClick={() => addOption(qIdx)}
                style={{ height: "24px", padding: "0 10px", borderRadius: "6px", border: `1px dashed ${COLORS.border}`, background: "transparent", color: COLORS.textSecondary, fontSize: "10px", fontWeight: "600", cursor: "pointer", marginTop: "2px" }}>
                + Add Option
              </button>
            </div>
            )}
            {/* VAS editable fields */}
            {q.response_type === "VAS" && (
              <div style={{ marginLeft: "32px" }}>
                <div style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "6px", textTransform: "uppercase" }}>VAS Slider Settings</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "6px", marginBottom: "6px" }}>
                  <div>
                    <label style={{ fontSize: "9px", color: COLORS.textSecondary, display: "block", marginBottom: "2px" }}>Min</label>
                    <input type="number" style={{ width: "100%", height: "28px", padding: "0 6px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px", boxSizing: "border-box" }}
                      value={q.vas_min ?? 0} onChange={e => updateQuestion(qIdx, "vas_min", parseInt(e.target.value) || 0)} />
                  </div>
                  <div>
                    <label style={{ fontSize: "9px", color: COLORS.textSecondary, display: "block", marginBottom: "2px" }}>Max</label>
                    <input type="number" style={{ width: "100%", height: "28px", padding: "0 6px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px", boxSizing: "border-box" }}
                      value={q.vas_max ?? 100} onChange={e => updateQuestion(qIdx, "vas_max", parseInt(e.target.value) || 100)} />
                  </div>
                  <div>
                    <label style={{ fontSize: "9px", color: COLORS.textSecondary, display: "block", marginBottom: "2px" }}>Min Label</label>
                    <input style={{ width: "100%", height: "28px", padding: "0 6px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "11px", boxSizing: "border-box" }}
                      value={q.vas_min_label || ""} onChange={e => updateQuestion(qIdx, "vas_min_label", e.target.value)} placeholder="e.g. Worst" />
                  </div>
                  <div>
                    <label style={{ fontSize: "9px", color: COLORS.textSecondary, display: "block", marginBottom: "2px" }}>Max Label</label>
                    <input style={{ width: "100%", height: "28px", padding: "0 6px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "11px", boxSizing: "border-box" }}
                      value={q.vas_max_label || ""} onChange={e => updateQuestion(qIdx, "vas_max_label", e.target.value)} placeholder="e.g. Best" />
                  </div>
                </div>
              </div>
            )}
            {/* FREE_TEXT info */}
            {q.response_type === "FREE_TEXT" && (
              <div style={{ marginLeft: "32px", padding: "6px 10px", background: COLORS.primaryLight, borderRadius: "6px", border: `1px solid ${COLORS.borderLight}` }}>
                <span style={{ fontSize: "11px", color: COLORS.primary }}>
                  📝 Free-text response — not included in score calculation
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Severity Bands */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <label style={{ fontSize: "13px", fontWeight: "600", color: COLORS.text }}>Severity Bands ({bands.length})</label>
          <button onClick={addBand}
            style={{ height: "28px", padding: "0 12px", borderRadius: "6px", border: "none", background: COLORS.primary, color: "#FFF", fontSize: "11px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
            <IconPlus /> Add Band
          </button>
        </div>
        {bands.map((b, bIdx) => (
          <div key={b.id || b._key || bIdx} style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "6px" }}>
            <input style={{ width: "120px", height: "32px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px" }}
              value={b.label} onChange={e => updateBand(bIdx, "label", e.target.value)} placeholder="Label (e.g., Mild)" />
            <input type="number" style={{ width: "60px", height: "32px", padding: "0 6px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px", textAlign: "center" }}
              value={b.min_score} onChange={e => updateBand(bIdx, "min_score", e.target.value)} placeholder="Min" />
            <span style={{ fontSize: "12px", color: COLORS.textSecondary }}>–</span>
            <input type="number" style={{ width: "60px", height: "32px", padding: "0 6px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px", textAlign: "center" }}
              value={b.max_score} onChange={e => updateBand(bIdx, "max_score", e.target.value)} placeholder="Max" />
            <div style={{ display: "flex", gap: "3px" }}>
              {bandColors.map(c => (
                <button key={c} onClick={() => updateBand(bIdx, "color", c)}
                  style={{ width: "22px", height: "22px", borderRadius: "6px", border: b.color === c ? `2px solid ${COLORS.text}` : `1px solid ${COLORS.border}`, background: c, cursor: "pointer" }} />
              ))}
            </div>
            <button onClick={() => removeBand(bIdx)}
              style={{ height: "28px", width: "28px", borderRadius: "6px", border: `1px solid ${COLORS.dangerBorder}`, background: COLORS.dangerLight, color: COLORS.danger, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px" }}>✕</button>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: "8px" }}>
        <button onClick={handleSubmit} disabled={saving}
          style={{ height: "38px", padding: "0 20px", borderRadius: "8px", border: "none", background: COLORS.accent, color: "#FFF", fontSize: "13px", fontWeight: "600", cursor: saving ? "wait" : "pointer", opacity: saving ? 0.6 : 1 }}>
          {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Scale"}
        </button>
        <button onClick={onCancel}
          style={{ height: "38px", padding: "0 16px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "13px", fontWeight: "500", cursor: "pointer" }}>
          Cancel
        </button>
      </div>
    </div>
  );
}


// ─── Settings Screen ─────────────────────────────────────────

